/**
 * useChatSession — manages the chat conversation state.
 *
 * Responsibilities:
 *   - Message history (user + assistant messages)
 *   - Intent detection + param collection via chatEngine
 *   - Follow-up question flow (collecting missing params)
 *   - Calling n8n via agentClient when params are complete
 *   - Email context with chunking (not full body)
 *   - Conversation memory for context continuity
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { callAgent } from '../../services/agentClient';
import { executeAction } from '../../services/actionClient';
import {
  resolveFollowUp,
  buildChunkedEmail,
  getIntentDef,
} from '../../services/chatEngine';
import { classifyUserIntent } from '../../services/intentClassifier';
import { TRIGGER_TYPES, ACTION_STATUS, N8N_PROXY_BASE } from '../../shared/constants';

// Message types
const MSG = {
  USER: 'user',
  ASSISTANT: 'assistant',
  SYSTEM: 'system',
};

let messageIdCounter = 0;
function nextId() {
  return `msg-${Date.now()}-${++messageIdCounter}`;
}

/**
 * Create a message object.
 */
function createMessage(role, text, extras = {}) {
  return {
    id: nextId(),
    role,
    text,
    timestamp: new Date().toISOString(),
    ...extras,
  };
}

/**
 * useChatSession hook.
 *
 * @param {Object} params
 * @param {Object} params.email - Current email from useMailboxItem
 * @param {Object} params.config - User config from useRoamingConfig
 * @returns {Object} Chat session state + actions
 */
export function useChatSession({ email, config }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionStates, setActionStates] = useState({});

  // Pending follow-up state: { intent, params, followUp }
  const pendingRef = useRef(null);
  const abortRef = useRef(null);

  // Track the current email ID so we can auto-clear chat on email switch
  const prevEmailIdRef = useRef(email?.id ?? null);

  // Auto-clear chat when the user switches to a different email
  useEffect(() => {
    const currentId = email?.id ?? null;
    if (prevEmailIdRef.current !== null && currentId !== null && currentId !== prevEmailIdRef.current) {
      // Email changed — reset the conversation
      console.log('[useChatSession] email changed, clearing chat. Old:', prevEmailIdRef.current, 'New:', currentId);
      setMessages([]);
      setActionStates({});
      pendingRef.current = null;
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      setLoading(false);
    }
    prevEmailIdRef.current = currentId;
  }, [email?.id]);

  /**
   * Add a message to the conversation.
   */
  const addMessage = useCallback((msg) => {
    setMessages((prev) => [...prev, msg]);
  }, []);

  /**
   * Send a user message and process it.
   */
  const sendMessage = useCallback(
    async (text) => {
      if (!text.trim()) return;

      // Add user message
      const userMsg = createMessage(MSG.USER, text);
      setMessages((prev) => [...prev, userMsg]);

      // ── Step 1: Check if this is a follow-up answer ──
      if (pendingRef.current) {
        const { intent, params: collectedParams, followUp } = pendingRef.current;
        const resolution = resolveFollowUp(text, followUp);

        if (resolution.resolved) {
          const mergedParams = { ...collectedParams, [resolution.key]: resolution.value };

          // Check if there are still missing params
          const def = getIntentDef(intent);
          const stillMissing = (def?.requiredParams || []).filter(
            (p) => !mergedParams[p]
          );

          if (stillMissing.length > 0) {
            // Ask for the next missing param
            const nextDetection = detectIntent(text, mergedParams);
            pendingRef.current = {
              intent,
              params: mergedParams,
              followUp: nextDetection.followUpQuestion || { text: `Please provide: ${stillMissing[0]}`, paramKey: stillMissing[0] },
            };
            const followMsg = createMessage(MSG.ASSISTANT, pendingRef.current.followUp.text, {
              options: pendingRef.current.followUp.options || null,
            });
            setMessages((prev) => [...prev, followMsg]);
            return;
          }

          // All params collected — execute
          pendingRef.current = null;
          await executeIntent(intent, mergedParams);
          return;
        } else {
          // Couldn't resolve — re-ask
          const reaskMsg = createMessage(
            MSG.ASSISTANT,
            `I didn't catch that. ${followUp.text}`,
            { options: followUp.options || null }
          );
          setMessages((prev) => [...prev, reaskMsg]);
          return;
        }
      }

      // ── Step 2+3: Unified intent classification (regex-first, AI fallback) ──
      console.group('[useChatSession] sendMessage:', text);
      console.log('[useChatSession] email context:', email ? { id: email.id, subject: email.subject } : 'none');
      console.log('[useChatSession] config.n8nUrl:', config.n8nUrl);
      console.log('[useChatSession] config.persona:', config.persona);

      const classification = await classifyUserIntent(text, {
        emailContext: email ? { subject: email.subject, bodyText: email.bodyText } : {},
        n8nBaseUrl: config.n8nUrl ? config.n8nUrl.replace(/\/webhook(?:-test)?\/.*$/, '') : N8N_PROXY_BASE,
      });

      console.log('[useChatSession] classification result:', {
        type: classification.type,
        source: classification.source,
        intents: classification.intents,
        primaryIntent: classification.primaryIntent,
        confidence: classification.confidence,
        params: classification.params,
        missingParams: classification.missingParams,
        aiTokens: classification.aiTokens,
      });

      // Greeting / help
      if (classification.type === 'greeting' || classification.type === 'help') {
        console.log('[useChatSession] → greeting/help response');
        console.groupEnd();
        const assistantMsg = createMessage(MSG.ASSISTANT, classification.greetingResponse);
        setMessages((prev) => [...prev, assistantMsg]);
        return;
      }

      // No intent detected (even after AI fallback)
      if (classification.type === 'fallback' || !classification.primaryIntent) {
        console.warn('[useChatSession] → NO INTENT DETECTED (fallback)');
        console.groupEnd();
        const source = classification.source === 'ai' ? ' I even asked my AI brain' : '';
        const fallbackMsg = createMessage(
          MSG.ASSISTANT,
          "I'm not sure what you'd like me to do." + (source ? source + ', but couldn\'t figure it out.' : '') + " Try saying:\n\n" +
            '- "Summarize this email"\n' +
            '- "Translate to Spanish"\n' +
            '- "Draft a reply"\n' +
            '- "What are the action items?"\n' +
            '- "Write an email about..."',
        );
        setMessages((prev) => [...prev, fallbackMsg]);
        return;
      }

      // ── Step 4: Check for missing params ──
      if (classification.missingParams.length > 0 && classification.followUpQuestion) {
        console.log('[useChatSession] → missing params, asking follow-up:', classification.missingParams);
        console.groupEnd();
        pendingRef.current = {
          intent: classification.primaryIntent,
          params: classification.params,
          followUp: classification.followUpQuestion,
        };
        const followMsg = createMessage(MSG.ASSISTANT, classification.followUpQuestion.text, {
          options: classification.followUpQuestion.options || null,
        });
        setMessages((prev) => [...prev, followMsg]);
        return;
      }

      // ── Step 5: All params present — execute ──
      console.log('[useChatSession] → executing intent:', classification.primaryIntent, 'all intents:', classification.intents, 'params:', classification.params);
      console.groupEnd();
      await executeIntent(classification.primaryIntent, classification.params, classification.intents);
    },
    [email, config]
  );

  /**
   * Execute an intent by calling the n8n webhook.
   */
  const executeIntent = useCallback(
    async (intent, params, allIntents = [intent]) => {
      console.group('[useChatSession] executeIntent:', intent, '| all intents:', allIntents);
      console.log('[useChatSession] params:', params);
      console.log('[useChatSession] config.n8nUrl:', config.n8nUrl);
      console.log('[useChatSession] config.persona:', config.persona);
      console.log('[useChatSession] email:', email ? { id: email.id, subject: email.subject, hasBody: !!(email.bodyText) } : 'null');

      // Abort any in-flight request
      if (abortRef.current) {
        console.warn('[useChatSession] aborting previous in-flight request');
        abortRef.current.abort();
      }
      const controller = new AbortController();
      abortRef.current = controller;

      // Show processing indicator
      const def = getIntentDef(intent);
      console.log('[useChatSession] intentDef:', def ? { description: def.description, requiredParams: def.requiredParams } : 'null');
      const processingMsg = createMessage(MSG.ASSISTANT, null, {
        isLoading: true,
        loadingText: `Working on it... ${def?.description || intent}`,
      });
      setMessages((prev) => [...prev, processingMsg]);
      setLoading(true);

      try {
        // Build chunked email (only relevant chunks, not full body)
        const chunkedEmail = buildChunkedEmail(email, intent);
        console.log('[useChatSession] chunkedEmail:', chunkedEmail ? { subject: chunkedEmail.subject, bodyLength: chunkedEmail.bodyText?.length, _chunkInfo: chunkedEmail._chunkInfo } : 'null');

        // Build config for this intent — pass ALL detected intents so n8n runs them all
        const intentConfig = {
          ...config,
          enabledIntents: allIntents.length > 0 ? allIntents : [intent],
          ...params,
        };
        console.log('[useChatSession] intentConfig:', { enabledIntents: intentConfig.enabledIntents, targetLanguage: intentConfig.targetLanguage, replyTone: intentConfig.replyTone });

        const trigger =
          intent === 'compose' ? TRIGGER_TYPES.COMPOSE : TRIGGER_TYPES.MANUAL;

        // Only use the persona's active workflow for auto-trigger (mail_opened).
        // For chat-driven intents, the user's detected intents should drive routing
        // via enabledIntents — not the persona's hardcoded workflow.
        const isAutoTrigger = trigger === TRIGGER_TYPES.MAIL_OPENED;
        const activeWorkflowId = isAutoTrigger
          ? ((config.activeWorkflow || {})[config.persona] || '')
          : '';
        console.log('[useChatSession] calling callAgent with:', { n8nUrl: config.n8nUrl, persona: config.persona, trigger, workflowId: activeWorkflowId, isAutoTrigger });

        const response = await callAgent({
          n8nUrl: config.n8nUrl,
          email: chunkedEmail,
          config: intentConfig,
          persona: config.persona,
          trigger,
          workflowId: activeWorkflowId,
          signal: controller.signal,
        });

        console.log('[useChatSession] SUCCESS:', { cards: response.cards?.length, intent: response.intent, latencyMs: response.telemetry?.clientLatencyMs });
        console.groupEnd();

        // Replace loading message with result
        setMessages((prev) => {
          const updated = prev.filter((m) => m.id !== processingMsg.id);
          const resultMsg = createMessage(MSG.ASSISTANT, def?.description || 'Done!', {
            cards: response.cards,
            telemetry: response.telemetry,
            intent,
          });
          return [...updated, resultMsg];
        });
      } catch (err) {
        if (err.name === 'AbortError') {
          console.warn('[useChatSession] request aborted by user/system');
          console.groupEnd();
          return;
        }

        console.error('[useChatSession] FAILED:', err.name, err.message);
        console.error('[useChatSession] error stack:', err.stack);
        console.groupEnd();

        // Replace loading message with error
        setMessages((prev) => {
          const updated = prev.filter((m) => m.id !== processingMsg.id);
          const errorMsg = createMessage(
            MSG.ASSISTANT,
            `Something went wrong: ${err.message}\n\nYou can try again or rephrase your request.`,
            { isError: true }
          );
          return [...updated, errorMsg];
        });
      } finally {
        setLoading(false);
      }
    },
    [email, config]
  );

  /**
   * Execute a Phase 2 action item.
   */
  const executeActionItem = useCallback(
    async ({ actionId, actionText }) => {
      setActionStates((prev) => ({
        ...prev,
        [actionId]: { status: ACTION_STATUS.PROCESSING, result: null, error: null },
      }));

      try {
        const n8nBaseUrl = config.n8nUrl.replace(/\/webhook(?:-test)?\/.*$/, '');
        const response = await executeAction({
          n8nBaseUrl,
          actionId,
          actionText,
          emailContext: email ? { id: email.id, subject: email.subject, from: email.from } : {},
        });

        setActionStates((prev) => ({
          ...prev,
          [actionId]: {
            status: response.status || ACTION_STATUS.COMPLETED,
            result: response.result,
            error: null,
          },
        }));

        // Add completion message to chat
        const statusMsg = createMessage(
          MSG.ASSISTANT,
          `Action "${actionText}" ${response.status === 'completed' ? 'completed successfully' : response.status}.`,
          { isActionResult: true }
        );
        setMessages((prev) => [...prev, statusMsg]);
      } catch (err) {
        setActionStates((prev) => ({
          ...prev,
          [actionId]: { status: ACTION_STATUS.FAILED, result: null, error: err.message },
        }));

        const errorMsg = createMessage(
          MSG.ASSISTANT,
          `Failed to execute "${actionText}": ${err.message}`,
          { isError: true }
        );
        setMessages((prev) => [...prev, errorMsg]);
      }
    },
    [config, email]
  );

  /**
   * Select a quick-option (from follow-up question options).
   */
  const selectOption = useCallback(
    (optionLabel) => {
      sendMessage(optionLabel);
    },
    [sendMessage]
  );

  /**
   * Cancel current operation.
   */
  const cancel = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    pendingRef.current = null;
    setLoading(false);
  }, []);

  /**
   * Clear the conversation and start fresh.
   */
  const clearChat = useCallback(() => {
    setMessages([]);
    pendingRef.current = null;
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setLoading(false);
    setActionStates({});
  }, []);

  return {
    messages,
    loading,
    actionStates,
    sendMessage,
    selectOption,
    executeActionItem,
    cancel,
    clearChat,
  };
}

export default useChatSession;
